import React, { Fragment } from "react";
import { graphql, Link, navigate, useStaticQuery } from "gatsby";
import Img from "gatsby-image";
import starSVG from "./../images/star.svg";
import { DraftboxPostCardData } from "../models/all-draftbox-post-description.model";
import classNames from "classnames";

type PostCardTypes = {
  post: DraftboxPostCardData;
  index: number;
};

type DraftboxSettingsQuery = {
  draftboxSettings: {
    content: {
      blogfeed: {
        blogCardBackgroundType: "firstLetter" | "icon";
        blogCardBackgroundColor: string;
        postCardOnHover:
          | "cursorPointer"
          | "stackedShadowAnimation"
          | "bottomBorderAnimation";
        postCardShowBorder: boolean;
      };
    };
  };
};

const PostCard: React.FC<PostCardTypes> = ({ post, index }) => {
  const {
    draftboxSettings: {
      content: {
        blogfeed: {
          blogCardBackgroundColor,
          blogCardBackgroundType,
          postCardShowBorder,
          postCardOnHover,
        },
      },
    },
  } = useStaticQuery<DraftboxSettingsQuery>(graphql`
    query {
      draftboxSettings {
        content {
          blogfeed {
            blogCardBackgroundType
            blogCardBackgroundColor
            postCardOnHover
            postCardShowBorder
          }
        }
      }
    }
  `);

  let excerpt = "";

  if (post.customExcerpt) {
    excerpt =
      post.customExcerpt.length > 140
        ? post.customExcerpt.slice(0, 140) + "..."
        : post.customExcerpt;
  } else if (post.excerpt) {
    excerpt =
      post.excerpt.length > 137
        ? post.excerpt.slice(0, 140) + "..."
        : post.excerpt;
  } else {
    excerpt = "";
  }

  const handleNavigation = (e: any, slug) => {
    e.stopPropagation();
    navigate(slug);
  };
  return (
    <div
      onClick={(e) => handleNavigation(e, `/${post.postSeoSlug}`)}
      className={classNames(
        "w-full md:w-1/2 lg:w-1/3 px-4 py-12 cursor-pointer relative post-card",
        {
          "post-card-big": index % 6 === 0,
          "post-card-stacked-animation":
            postCardOnHover === "stackedShadowAnimation",
          "post-card-border-animation":
            postCardOnHover === "bottomBorderAnimation",
        }
      )}
    >
      <div
        className={classNames("h-full rounded flex", {
          "flex-col lg:flex-row": index % 6 === 0,
          "flex-col justify-between": index % 6 !== 0,
        })}
      >
        <div className="post-card-image-container relative">
          {post.featured && (
            <span
              className="absolute rounded text-xs lg:text-sm bg-yellow-200 text-yellow-800 px-2 py-1 font-sansSemibold z-10 flex items-center"
              style={{ right: "10px", top: "10px", lineHeight: "1.2" }}
            >
              ★ Featured
            </span>
          )}
          {post.featureImage?.childImageSharp && (
            <Img
              className={classNames(
                "post-card-image mb-4 h-auto w-full object-cover",
                {
                  "post-card-border": postCardShowBorder,
                }
              )}
              fluid={post.featureImage.childImageSharp.fluid}
            />
          )}
          {post.featureImage && !post.featureImage.childImageSharp && (
            <div className="post-card-image mb-4 relative">
              <img
                height="200"
                width="auto"
                className={classNames("absolute inset-0 h-full w-full object-cover", {
                  "post-card-border": postCardShowBorder,
                })}
                src={post.featureImage?.publicURL}
                alt={post.title}
              />
            </div>
          )}
          {!post.featureImage && (
            <div className="post-card-image mb-4 relative">
              <div
                style={{ backgroundColor: blogCardBackgroundColor }}
                className={classNames(
                  "absolute inset-0 flex justify-center items-center text-white font-sansBold w-full object-cover",
                  {
                    "post-card-border": postCardShowBorder,
                  }
                )}
              >
                {/* <div
                style={{
                  fontSize: "6rem",
                  backgroundColor: blogCardBackgroundColor,
                }}
                dangerouslySetInnerHTML={{ __html: post.title[0] }}
              ></div> */}
                <div className="h-16 w-16">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="post-card-content flex flex-col">
          {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
          <div className="mb-4">
            <p className="text-sm text-gray-700 break-words py-2 lg:py-0">
              {post.primaryTag && (
                <>
                  <span>
                    <a
                      onClick={(e) =>
                        handleNavigation(e, `/tag/${post.primaryTag.slug}`)
                      }
                      className="no-underline hover:underline font-sansSemibold text-primary uppercase tracking-wider"
                    >
                      {post.primaryTag.name}
                    </a>
                  </span>
                </>
              )}
            </p>
            <div className="my-2">
              <Link
                to={`/${post.postSeoSlug}`}
                className="text-2xl font-sansSemibold tracking-tight leading-tight break-words text-gray-900"
                dangerouslySetInnerHTML={{ __html: post.title }}
              ></Link>
            </div>
            <p
              className="post-card-excerpt break-words font-serifLight text-gray-700"
              dangerouslySetInnerHTML={{ __html: excerpt }}
            ></p>
          </div>

          <div className="flex justify-between mt-4">
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-4">
                {post.authors &&
                  post.authors.map((author, index) => {
                    return (
                      <Fragment key={index}>
                        {author.profileImage ? (
                          <a
                            className="h-10 w-10"
                            onClick={(e) =>
                              handleNavigation(e, `/author/${author.slug}`)
                            }
                          >
                            {!author.profileImage.childImageSharp && (
                              <img
                                height="40"
                                width="40"
                                key={index}
                                className="h-full w-full rounded-full object-cover"
                                src={author.profileImage?.publicURL}
                                alt=""
                              />
                            )}
                            {author.profileImage.childImageSharp && (
                              <Img
                                className="h-full w-full rounded-full object-cover"
                                fixed={
                                  author.profileImage.childImageSharp.fixed
                                }
                              ></Img>
                            )}
                          </a>
                        ) : (
                          <a
                            onClick={(e) =>
                              handleNavigation(e, `/author/${author.slug}`)
                            }
                          >
                            <div className="w-10 h-10 bg-gray-500 border text-white rounded-full flex justify-center items-center mx-auto">
                              <i className="far fa-user"></i>
                            </div>
                          </a>
                        )}
                      </Fragment>
                    );
                  })}
              </div>
              <div className="flex flex-col">
                <div className="flex font-sansNormal">
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "220px",
                    }}
                  >
                    {post.authors?.length < 3 &&
                      post.authors.map((author, index) => {
                        return (
                          <a
                            key={index}
                            onClick={(e) =>
                              handleNavigation(e, `/author/${author.slug}`)
                            }
                            className="no-underline hover:underline text-sm text-gray-700 mr-1 font-sansMedium"
                          >
                            <>
                              {author.name}
                              {index !== post.authors.length - 1 ? "," : ""}
                            </>
                          </a>
                        );
                      })}

                    {post.authors?.length > 3 && (
                      <span className="no-underline text-sm text-gray-700 font-sansMedium">
                        Multiple Authors
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex">
                  <span className="text-xs text-gray-700">
                    {post.publishedAt} • {post.readingTimeStr}
                  </span>
                </div>
              </div>
            </div>
            {/* <span className="text-gray-700 text-sm">{post.publishedAt}</span> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCard;
